/**
 * A scroll progress indicator for the resource-list carousels.
 *
 * The carousels are native scroll containers (`slideshow-slides` is
 * `overflow-x: scroll` with scroll snapping), so progress is read straight off
 * the element rather than tracked against a slide index. That keeps the bar
 * accurate mid-swipe, and correct when several cards are visible at once.
 *
 * Two custom properties are published on the host element:
 *   --carousel-progress-size  the share of the track the thumb occupies
 *   --carousel-progress-ratio how far through the scroll we are, 0 to 1
 */
class CarouselProgress extends HTMLElement {
  connectedCallback() {
    this.scroller = this.closest('.resource-list__carousel')?.querySelector('slideshow-slides');
    if (!this.scroller) return;

    this.update = this.update.bind(this);
    this.scroller.addEventListener('scroll', this.update, { passive: true });

    // Slide widths are container-query driven, so a resize changes how much is
    // scrollable without any scroll event firing.
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.scroller);
    }

    this.update();
  }

  disconnectedCallback() {
    this.scroller?.removeEventListener('scroll', this.update);
    this.resizeObserver?.disconnect();
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
    const scrollable = scrollWidth - clientWidth;

    // Nothing to scroll — every card already fits, so the bar would be noise.
    if (scrollable <= 1) {
      this.setAttribute('hidden', '');
      return;
    }
    this.removeAttribute('hidden');

    const size = Math.min(clientWidth / scrollWidth, 1);
    // Math.abs keeps this correct in RTL, where scrollLeft counts negative.
    const ratio = Math.min(Math.abs(scrollLeft) / scrollable, 1);

    this.style.setProperty('--carousel-progress-size', `${size * 100}%`);
    this.style.setProperty('--carousel-progress-ratio', `${ratio}`);
  }
}

if (!customElements.get('carousel-progress')) {
  customElements.define('carousel-progress', CarouselProgress);
}
